import { Box, HStack, Text, VStack, Badge, Spinner } from '@chakra-ui/react'
import { motion, AnimatePresence } from 'framer-motion'
import { useState, useEffect, useRef } from 'react'
import { api } from '../../../../api/api'

const MotionBox = motion(Box)

export interface SlashCommandResult {
  id: string
  title: string
  tags: string[]
  type: 'item' | 'saw'
}

interface SlashCommandMenuProps {
  command: '/item' | '/saw' | null
  searchQuery: string
  onSelect: (result: SlashCommandResult) => void
  onClose: () => void
}

const TAG_COLORS: Record<string, string> = {
  counterfeit: 'red',
  suspicious: 'orange',
  verified: 'green',
  takedown: 'purple',
  monitoring: 'blue',
}

const SlashCommandMenu = ({ command, searchQuery, onSelect, onClose }: SlashCommandMenuProps) => {
  const [results, setResults] = useState<SlashCommandResult[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!command) return
    setIsLoading(true)
    setSelectedIndex(0)

    const endpoint = command === '/item' ? '/items/' : '/saw/sites/'
    const params: Record<string, string> = { page_size: '10' }
    if (searchQuery) params.search = searchQuery

    const timer = setTimeout(() => {
      api
        .get(endpoint, { params })
        .then((res) => {
          const data = res.data?.results || res.data || []
          const mapped: SlashCommandResult[] = data.map((item: any) => ({
            id: item.id,
            title: item.title || item.domain || item.name || item.url || item.id,
            tags: [item.group_tag, item.group_tag_2, item.group_tag_3].filter(Boolean),
            type: command === '/item' ? ('item' as const) : ('saw' as const),
          }))
          setResults(mapped)
        })
        .catch(() => setResults([]))
        .finally(() => setIsLoading(false))
    }, 300)

    return () => clearTimeout(timer)
  }, [command, searchQuery])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!command) return
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSelectedIndex((prev) => Math.min(prev + 1, results.length - 1))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSelectedIndex((prev) => Math.max(prev - 1, 0))
      } else if (e.key === 'Enter' && results.length > 0) {
        e.preventDefault()
        onSelect(results[selectedIndex])
      } else if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [command, results, selectedIndex, onSelect, onClose])

  if (!command) return null

  return (
    <AnimatePresence>
      <MotionBox
        ref={menuRef}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 8 }}
        transition={{ duration: 0.15 }}
        position="absolute"
        bottom="100%"
        left={0}
        right={0}
        mb={2}
        mx={4}
        bg="white"
        borderRadius="12px"
        boxShadow="0 4px 24px rgba(0,0,0,0.12)"
        border="1px solid"
        borderColor="gray.100"
        maxH="280px"
        overflowY="auto"
        zIndex={20}
      >
        <Box px={3} py={2} borderBottom="1px solid" borderColor="gray.50">
          <Text fontSize="11px" fontWeight={600} color="gray.400" textTransform="uppercase" letterSpacing="0.05em">
            {command === '/item' ? 'Select Item' : 'Select Website (SAW)'}
          </Text>
        </Box>

        {isLoading ? (
          <Box px={3} py={4} textAlign="center">
            <Spinner size="sm" color="gray.400" />
          </Box>
        ) : results.length === 0 ? (
          <Box px={3} py={3}>
            <Text fontSize="13px" color="gray.400">
              {searchQuery ? 'No results found' : 'Type to search...'}
            </Text>
          </Box>
        ) : (
          <VStack align="stretch" gap={0} py={1}>
            {results.map((result, index) => (
              <HStack
                key={result.id}
                px={3}
                py={2}
                cursor="pointer"
                bg={index === selectedIndex ? 'gray.50' : 'transparent'}
                _hover={{ bg: 'gray.50' }}
                onClick={() => onSelect(result)}
                gap={2}
              >
                <VStack align="start" gap={0.5} flex={1} minW={0}>
                  <Text fontSize="13px" fontWeight={500} color="gray.700" lineClamp={1}>
                    {result.title}
                  </Text>
                  <Text fontSize="11px" color="gray.400" fontFamily="mono">
                    {result.id.slice(0, 8)}...
                  </Text>
                </VStack>
                <HStack gap={1} flexShrink={0} flexWrap="wrap">
                  {result.tags.map((tag) => (
                    <Badge
                      key={tag}
                      size="sm"
                      colorScheme={TAG_COLORS[tag.toLowerCase()] || 'gray'}
                      fontSize="10px"
                      fontWeight={500}
                    >
                      {tag}
                    </Badge>
                  ))}
                </HStack>
              </HStack>
            ))}
          </VStack>
        )}
      </MotionBox>
    </AnimatePresence>
  )
}

export default SlashCommandMenu
